// Server dates arrive as "dd/mm/yyyy HH:mm:ss" strings expressed in UTC.
export const parseServerDateTime = (value: any): Date | null => {
  if (typeof value !== 'string' || !value.includes('/')) {
    return null;
  }

  const [datePart, timePart] = value.split(' ');
  const [day, month, year] = (datePart ?? '').split('/');
  const [hour, minute, second] = (timePart ?? '00:00:00').split(':');

  if (!day || !month || !year) {
    return null;
  }

  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour ?? 0), Number(minute ?? 0), Number(second ?? 0)));
  return Number.isNaN(date.getTime()) ? null : date;
};

export const hoursSince = (date: Date | null): number | null => {
  if (!date) {
    return null;
  }
  return (Date.now() - date.getTime()) / (1000 * 60 * 60);
};

// Accepts both the "dd/mm/yyyy HH:mm:ss" server format and ISO strings.
export const parseAnyDateTime = (value: any): Date | null => {
  const serverDate = parseServerDateTime(value);
  if (serverDate) {
    return serverDate;
  }
  if (typeof value !== 'string' || value.length === 0) {
    return null;
  }
  // ISO strings without an explicit offset are UTC on the server side.
  const hasOffset = /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);
  const date = new Date(hasOffset ? value : `${value}Z`);
  if (Number.isNaN(date.getTime())) {
    const fallback = new Date(value);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }
  return date;
};

// /alarm/list responses have been observed both as a plain array and as { items: [] }.
export const extractAlarmItems = (responseData: any): any[] => {
  const raw = responseData?.data;
  if (Array.isArray(raw)) {
    return raw;
  }
  if (Array.isArray(raw?.items)) {
    return raw.items;
  }
  return [];
};

export const getAlarmEventTypeName = (alarm: any): string => {
  return alarm?.eventType?.eventTypeName ?? alarm?.event?.eventType?.name ?? '';
};

export const getAlarmZoneName = (alarm: any): string => {
  return alarm?.zoneName ?? alarm?.event?.zoneName ?? '';
};

export const averageOf = (values: number[]): number | null => {
  if (values.length === 0) {
    return null;
  }
  return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
};

// Maps a 0-100 battery percentage onto a red (low) to green (high) hue gradient.
export const getBatteryGradientColor = (percentage: number): string => {
  const clamped = Math.max(0, Math.min(100, percentage));
  const hue = (clamped / 100) * 120;
  return `hsl(${hue}, 72%, 40%)`;
};

export const formatRelativeTime = (date: Date | null, languageId: string | null): string => {
  const isItalian = languageId === 'it-IT';

  if (!date) {
    return isItalian ? 'Nessun dato' : 'No data';
  }

  const diffMinutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (diffMinutes < 1) {
    return isItalian ? 'Adesso' : 'Just now';
  }
  if (diffMinutes < 60) {
    return isItalian ? `${diffMinutes} min fa` : `${diffMinutes} min ago`;
  }
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return isItalian ? `${diffHours}h fa` : `${diffHours}h ago`;
  }
  const diffDays = Math.floor(diffHours / 24);
  return isItalian ? `${diffDays}g fa` : `${diffDays}d ago`;
};
